import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { api } from '../lib/api';

type Tab = 'models' | 'types' | 'prompts' | 'bindings' | 'users' | 'stats';

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('models');
  return (
    <Layout>
      <div className="mb-4 flex flex-wrap gap-2">
        {([
          ['models', '模型配置'],
          ['types', '题型'],
          ['prompts', '提示词'],
          ['bindings', '绑定'],
          ['users', '用户'],
          ['stats', 'Token 统计'],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={
              'h-9 rounded-md px-3 text-sm font-medium transition-colors ' +
              (tab === k
                ? 'bg-sky-600 text-white'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
            }
          >
            {label}
          </button>
        ))}
      </div>
      <div>
        {tab === 'models' && <ModelsTab />}
        {tab === 'types' && <TypesTab />}
        {tab === 'prompts' && <PromptsTab />}
        {tab === 'bindings' && <BindingsTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'stats' && <StatsTab />}
      </div>
    </Layout>
  );
}

// ---------------- Models ----------------
type ApiType = 'openai-chat' | 'openai-responses' | 'anthropic';
type Model = {
  id: number;
  name: string;
  base_url: string;
  model_id: string;
  api_key_masked: string;
  api_type: ApiType;
  is_active: boolean;
  is_default: boolean;
  notes: string | null;
  created_at: number;
  updated_at: number;
};

function ModelsTab() {
  const [items, setItems] = useState<Model[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Model | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = async () => {
    const r = await api<{ items: Model[] }>('/api/v1/admin/models');
    if (r.ok) setItems(r.data.items);
    else setErr(r.error.message);
  };
  useEffect(() => {
    reload();
  }, []);

  const remove = async (id: number) => {
    if (!confirm('确定删除该模型?')) return;
    await api(`/api/v1/admin/models/${id}`, { method: 'DELETE' });
    reload();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">模型配置</h2>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="h-9 rounded-md bg-sky-600 px-3 text-sm font-medium text-white hover:bg-sky-700"
        >
          新增
        </button>
      </div>
      {err && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">名称</th>
              <th className="px-3 py-2 text-left">API 类型</th>
              <th className="px-3 py-2 text-left">base_url</th>
              <th className="px-3 py-2 text-left">model_id</th>
              <th className="px-3 py-2 text-left">API key</th>
              <th className="px-3 py-2 text-left">启用</th>
              <th className="px-3 py-2 text-left">默认</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((it) => (
              <tr key={it.id}>
                <td className="px-3 py-2">{it.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{it.api_type}</td>
                <td className="px-3 py-2 font-mono text-xs">{it.base_url}</td>
                <td className="px-3 py-2 font-mono text-xs">{it.model_id}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{it.api_key_masked}</td>
                <td className="px-3 py-2">{it.is_active ? '✓' : '—'}</td>
                <td className="px-3 py-2">{it.is_default ? '✓' : '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(it);
                      setShowForm(true);
                    }}
                    className="mr-2 text-sky-600 hover:underline"
                  >
                    编辑
                  </button>
                  <button type="button" onClick={() => remove(it.id)} className="text-red-600 hover:underline">
                    删除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="p-6 text-center text-slate-500">暂无配置</p>}
      </div>
      {showForm && (
        <ModelForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

function ModelForm({ initial, onClose, onSaved }: { initial: Model | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [apiType, setApiType] = useState<ApiType>(initial?.api_type ?? 'openai-chat');
  const [baseUrl, setBaseUrl] = useState(initial?.base_url ?? '');
  const [modelId, setModelId] = useState(initial?.model_id ?? '');
  const [apiKey, setApiKey] = useState('');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [isDefault, setIsDefault] = useState(initial?.is_default ?? false);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setErr(null);
    const payload: Record<string, unknown> = {
      name: name.trim(),
      api_type: apiType,
      base_url: baseUrl.trim(),
      model_id: modelId.trim(),
      is_active: isActive,
      is_default: isDefault,
      notes: notes.trim() || null,
    };
    if (apiKey.trim()) payload.api_key = apiKey.trim();
    const r = initial
      ? await api(`/api/v1/admin/models/${initial.id}`, { method: 'PATCH', body: JSON.stringify(payload) })
      : await api('/api/v1/admin/models', { method: 'POST', body: JSON.stringify(payload) });
    if (!r.ok) setErr(r.error.message);
    else onSaved();
  };

  return (
    <div className="fixed inset-0 z-20 flex items-end justify-center bg-slate-900/50 sm:items-center">
      <div className="w-full max-w-lg space-y-4 rounded-t-xl bg-white p-6 shadow-lg sm:rounded-xl">
        <h3 className="text-lg font-semibold">{initial ? '编辑模型' : '新增模型'}</h3>
        <Field label="名称">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </Field>
        <Field label="API 类型">
          <select
            value={apiType}
            onChange={(e) => setApiType(e.target.value as ApiType)}
            className={inputCls}
          >
            <option value="openai-chat">OpenAI Chat (/v1/chat/completions)</option>
            <option value="openai-responses">OpenAI Responses (/v1/responses, gpt-5 推荐)</option>
            <option value="anthropic">Anthropic Messages (/v1/messages, Claude)</option>
          </select>
        </Field>
        <Field label="base_url (以 /v1 结尾)">
          <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} className={inputCls} placeholder="https://.../v1" />
        </Field>
        <Field label="model_id">
          <input value={modelId} onChange={(e) => setModelId(e.target.value)} className={inputCls} />
        </Field>
        <Field label={initial ? 'API key (留空保留旧值)' : 'API key'}>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className={inputCls}
            autoComplete="off"
          />
        </Field>
        <div className="flex gap-6">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            启用
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            设为默认
          </label>
        </div>
        <Field label="备注">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
        </Field>
        {err && <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{err}</div>}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="h-10 flex-1 rounded-md border border-slate-200 bg-white text-sm font-medium">
            取消
          </button>
          <button type="button" onClick={save} className="h-10 flex-1 rounded-md bg-sky-600 text-sm font-medium text-white hover:bg-sky-700">
            保存
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Types ----------------
type ExType = { id: number; code: string; name: string; description: string | null; is_active: boolean; sort_order: number };

function TypesTab() {
  const [items, setItems] = useState<ExType[]>([]);
  const reload = async () => {
    const r = await api<{ items: ExType[] }>('/api/v1/admin/exercise-types');
    if (r.ok) setItems(r.data.items);
  };
  useEffect(() => { reload(); }, []);
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">题型</h2>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">code</th>
              <th className="px-3 py-2 text-left">名称</th>
              <th className="px-3 py-2 text-left">描述</th>
              <th className="px-3 py-2 text-left">启用</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((it) => (
              <tr key={it.id}>
                <td className="px-3 py-2 font-mono">{it.code}</td>
                <td className="px-3 py-2">{it.name}</td>
                <td className="px-3 py-2 text-slate-500">{it.description}</td>
                <td className="px-3 py-2">{it.is_active ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">v1 只预置珠心算,扩展请通过 API。</p>
    </div>
  );
}

// ---------------- Prompts ----------------
type Prompt = { id: number; name: string; content: string; exercise_type_id: number; notes: string | null };

function PromptsTab() {
  const [items, setItems] = useState<Prompt[]>([]);
  const [types, setTypes] = useState<ExType[]>([]);
  const [editing, setEditing] = useState<Prompt | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const reload = async () => {
    const [p, t] = await Promise.all([
      api<{ items: Prompt[] }>('/api/v1/admin/prompts'),
      api<{ items: ExType[] }>('/api/v1/admin/exercise-types'),
    ]);
    if (p.ok) setItems(p.data.items);
    if (t.ok) setTypes(t.data.items);
  };
  useEffect(() => { reload(); }, []);
  const typeName = (id: number) => types.find((t) => t.id === id)?.name ?? `#${id}`;
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">提示词</h2>
      <ul className="space-y-3">
        {items.map((p) => {
          const isOpen = expanded.has(p.id);
          return (
            <li key={p.id} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="flex items-start gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{p.name}</span>
                    <span className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs text-sky-700">
                      {typeName(p.exercise_type_id)}
                    </span>
                  </div>
                  {p.notes && (
                    <div className="mt-1 text-xs text-slate-500">备注:{p.notes}</div>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                  <button onClick={() => toggle(p.id)} className="text-sm text-slate-600 hover:underline">
                    {isOpen ? '收起' : '展开'}
                  </button>
                  <button onClick={() => setEditing(p)} className="text-sm text-sky-600 hover:underline">编辑</button>
                </div>
              </div>
              <pre
                className={
                  'm-0 whitespace-pre-wrap break-words border-t border-slate-100 bg-slate-50/60 px-4 py-3 font-mono text-xs text-slate-700 ' +
                  (isOpen ? 'max-h-[600px]' : 'max-h-32') +
                  ' overflow-auto'
                }
              >
                {p.content}
              </pre>
            </li>
          );
        })}
      </ul>
      {editing && <PromptForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function PromptForm({ initial, onClose, onSaved }: { initial: Prompt; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial.name);
  const [content, setContent] = useState(initial.content);
  const [notes, setNotes] = useState(initial.notes ?? '');
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    const r = await api(`/api/v1/admin/prompts/${initial.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name, content, notes }),
    });
    if (!r.ok) setErr(r.error.message);
    else onSaved();
  };
  return (
    <div className="fixed inset-0 z-20 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4">
      <div className="my-6 flex max-h-[calc(100vh-3rem)] w-full max-w-4xl flex-col rounded-xl bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-semibold">编辑提示词</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="关闭"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <Field label="名称">
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </Field>
          <Field label={`内容 (${content.length} 字)`}>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="h-[60vh] w-full rounded-md border border-slate-200 bg-white p-3 font-mono text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />
          </Field>
          <Field label="备注">
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
          </Field>
          {err && <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{err}</div>}
        </div>
        <div className="flex gap-2 border-t border-slate-200 px-6 py-4">
          <button type="button" onClick={onClose} className="h-10 flex-1 rounded-md border border-slate-200 bg-white text-sm font-medium">取消</button>
          <button type="button" onClick={save} className="h-10 flex-1 rounded-md bg-sky-600 text-sm font-medium text-white hover:bg-sky-700">保存</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Bindings ----------------
type Binding = {
  id: number;
  exercise_type_id: number;
  model_config_id: number | null;
  prompt_id: number;
  exercise_type_name: string;
  model_name: string | null;
  prompt_name: string;
  created_at: number;
};

function BindingsTab() {
  const [items, setItems] = useState<Binding[]>([]);
  const [types, setTypes] = useState<ExType[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [showForm, setShowForm] = useState(false);
  const reload = async () => {
    const [b, t, m, p] = await Promise.all([
      api<{ items: Binding[] }>('/api/v1/admin/prompt-bindings'),
      api<{ items: ExType[] }>('/api/v1/admin/exercise-types'),
      api<{ items: Model[] }>('/api/v1/admin/models'),
      api<{ items: Prompt[] }>('/api/v1/admin/prompts'),
    ]);
    if (b.ok) setItems(b.data.items);
    if (t.ok) setTypes(t.data.items);
    if (m.ok) setModels(m.data.items);
    if (p.ok) setPrompts(p.data.items);
  };
  useEffect(() => { reload(); }, []);

  const remove = async (id: number) => {
    if (!confirm('删除该绑定?')) return;
    await api(`/api/v1/admin/prompt-bindings/${id}`, { method: 'DELETE' });
    reload();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">提示词绑定</h2>
        <button onClick={() => setShowForm(true)} className="h-9 rounded-md bg-sky-600 px-3 text-sm font-medium text-white hover:bg-sky-700">新增</button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">题型</th>
              <th className="px-3 py-2 text-left">模型</th>
              <th className="px-3 py-2 text-left">提示词</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((b) => (
              <tr key={b.id}>
                <td className="px-3 py-2">{b.exercise_type_name}</td>
                <td className="px-3 py-2">{b.model_name ?? <span className="text-slate-400">默认回落</span>}</td>
                <td className="px-3 py-2">{b.prompt_name}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => remove(b.id)} className="text-red-600 hover:underline text-sm">删除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <BindingForm
          types={types}
          models={models}
          prompts={prompts}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); reload(); }}
        />
      )}
    </div>
  );
}

function BindingForm({ types, models, prompts, onClose, onSaved }: { types: ExType[]; models: Model[]; prompts: Prompt[]; onClose: () => void; onSaved: () => void }) {
  const [typeId, setTypeId] = useState<number | ''>('');
  const [modelId, setModelId] = useState<number | ''>('');
  const [promptId, setPromptId] = useState<number | ''>('');
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    if (!typeId || !promptId) { setErr('题型和提示词必选'); return; }
    const r = await api('/api/v1/admin/prompt-bindings', {
      method: 'POST',
      body: JSON.stringify({ exercise_type_id: typeId, model_config_id: modelId || null, prompt_id: promptId }),
    });
    if (!r.ok) setErr(r.error.message);
    else onSaved();
  };
  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-lg">
        <h3 className="text-lg font-semibold">新增绑定</h3>
        <Field label="题型">
          <select value={typeId} onChange={(e) => setTypeId(Number(e.target.value) || '')} className={inputCls}>
            <option value="">--</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="模型 (留空 = 该题型默认回落)">
          <select value={modelId} onChange={(e) => setModelId(Number(e.target.value) || '')} className={inputCls}>
            <option value="">(默认回落)</option>
            {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
        <Field label="提示词">
          <select value={promptId} onChange={(e) => setPromptId(Number(e.target.value) || '')} className={inputCls}>
            <option value="">--</option>
            {prompts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        {err && <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{err}</div>}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="h-10 flex-1 rounded-md border border-slate-200 bg-white text-sm font-medium">取消</button>
          <button type="button" onClick={save} className="h-10 flex-1 rounded-md bg-sky-600 text-sm font-medium text-white hover:bg-sky-700">保存</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Users ----------------
type UserRow = { id: number; username: string; status: string; token_version: number; quota_total: number; quota_used: number; created_at: number };

function UsersTab() {
  const [items, setItems] = useState<UserRow[]>([]);
  const [showForm, setShowForm] = useState(false);
  const reload = async () => {
    const r = await api<{ items: UserRow[] }>('/api/v1/admin/users');
    if (r.ok) setItems(r.data.items);
  };
  useEffect(() => { reload(); }, []);

  const resetPwd = async (id: number) => {
    const pwd = prompt('输入新密码 (至少 8 位):');
    if (!pwd || pwd.length < 8) return;
    await api(`/api/v1/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify({ password: pwd }) });
    alert('已重置,该用户所有 token 作废');
    reload();
  };
  const toggleStatus = async (u: UserRow) => {
    const next = u.status === 'active' ? 'disabled' : 'active';
    await api(`/api/v1/admin/users/${u.id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
    reload();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">用户</h2>
        <button onClick={() => setShowForm(true)} className="h-9 rounded-md bg-sky-600 px-3 text-sm font-medium text-white hover:bg-sky-700">新增</button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">用户名</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">token ver</th>
              <th className="px-3 py-2 text-left">配额</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((u) => (
              <tr key={u.id}>
                <td className="px-3 py-2">{u.username}</td>
                <td className="px-3 py-2">
                  <span className={u.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}>
                    {u.status}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{u.token_version}</td>
                <td className="px-3 py-2 text-slate-500">{u.quota_used}/{u.quota_total}</td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button onClick={() => resetPwd(u.id)} className="text-sky-600 text-sm hover:underline">重置密码</button>
                  <button onClick={() => toggleStatus(u)} className="text-slate-600 text-sm hover:underline">
                    {u.status === 'active' ? '禁用' : '启用'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && <UserForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reload(); }} />}
    </div>
  );
}

function UserForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setErr(null);
    const r = await api('/api/v1/admin/users', {
      method: 'POST',
      body: JSON.stringify({ username: username.trim(), password }),
    });
    if (!r.ok) setErr(r.error.message);
    else onSaved();
  };
  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-lg">
        <h3 className="text-lg font-semibold">新增用户</h3>
        <Field label="用户名 (字母/数字/._-)">
          <input value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} />
        </Field>
        <Field label="密码 (≥ 8 位)">
          <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        </Field>
        {err && <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{err}</div>}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="h-10 flex-1 rounded-md border border-slate-200 bg-white text-sm font-medium">取消</button>
          <button type="button" onClick={save} className="h-10 flex-1 rounded-md bg-sky-600 text-sm font-medium text-white hover:bg-sky-700">保存</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Stats ----------------
type StatItem = { model_config_id: number; name: string; requests: number; tokens_in: number; tokens_out: number; failed: number };

function StatsTab() {
  const [items, setItems] = useState<StatItem[]>([]);
  const [range, setRange] = useState<'7d' | '30d' | 'all'>('30d');
  useEffect(() => {
    (async () => {
      const r = await api<{ items: StatItem[] }>(`/api/v1/admin/stats/tokens-by-model?range=${range}`);
      if (r.ok) setItems(r.data.items);
    })();
  }, [range]);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Token 消耗(按模型)</h2>
        <select value={range} onChange={(e) => setRange(e.target.value as '7d' | '30d' | 'all')} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="7d">7 天</option>
          <option value="30d">30 天</option>
          <option value="all">全部</option>
        </select>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">模型</th>
              <th className="px-3 py-2 text-right">请求</th>
              <th className="px-3 py-2 text-right">tokens_in</th>
              <th className="px-3 py-2 text-right">tokens_out</th>
              <th className="px-3 py-2 text-right">失败</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((s) => (
              <tr key={s.model_config_id}>
                <td className="px-3 py-2">{s.name}</td>
                <td className="px-3 py-2 text-right font-mono">{s.requests}</td>
                <td className="px-3 py-2 text-right font-mono">{s.tokens_in}</td>
                <td className="px-3 py-2 text-right font-mono">{s.tokens_out}</td>
                <td className="px-3 py-2 text-right font-mono text-red-600">{s.failed}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="p-6 text-center text-slate-500">暂无数据</p>}
      </div>
    </div>
  );
}

// ---------------- helpers ----------------
const inputCls =
  'h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-base font-medium text-slate-700">{label}</label>
      {children}
    </div>
  );
}
